import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Typography } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useState, useEffect } from 'react'
import dataService from '../../../services/data.service';
import Http from '../../../services/Http';
import { useHMG } from '../../../HMGContext';

const MarkCompleteDialog = ({ isOpen, rowData, showToast, setOpenMultipleMarkCompelte, onSuccessfulUpdate, setAllPlansPageReload}) => {
    const isMultipleData = Array.isArray(rowData);
    const property = !isMultipleData ? dataService.getPropertyById(rowData?.propertyId) || dataService.getPropertyByIds(rowData?.propertyIds?.[0]) : dataService.getPropertyByIds(rowData[0]?.propertyIds);
    // Add state for checkbox and loading
    const [isMarkComplete, setIsMarkComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [initialCompletedState, setInitialCompletedState] = useState(false);
    const {setCurrentModalName} = useHMG();

    // Check if data is already completed
    useEffect(() => {
        if (isMultipleData) {
            // For multiple data, check if all items have completedDateNumber
            const allCompleted = rowData.every(item => item.completedDateNumber != null);
            setIsMarkComplete(allCompleted);
            setInitialCompletedState(allCompleted);
        } else {
            // For single data
            const isCompleted = rowData?.completedDateNumber != null;
            setIsMarkComplete(isCompleted);
            setInitialCompletedState(isCompleted);
        }
    }, [rowData, isMultipleData]);

    const handelClose = () => {
        setOpenMultipleMarkCompelte(false);
        setCurrentModalName(null)
    }

    // Add function to get current date in YYYYMMDD format
    const getCurrentDateNumber = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    };

    // Add function to get plan IDs
    const getPlanIds = () => {
        if (isMultipleData) {
            return rowData.filter(item => item.asOnDate !== null && item.asOnDate !== undefined).map(item => item.id).join(',');
        } else {
            return rowData.asOnDate !== null && rowData.asOnDate !== undefined ? rowData.id : null;
        }
    };

    // Add API call function
    const handleUpdate = async () => {
        setIsLoading(true);
        try {
            const planIds = getPlanIds();

            //if no valid planIds, stop here
            if (!planIds || planIds.length === 0) {
                showToast('No valid plans selected to update.', 'error');
                handelClose();
                return;
            }

            let apiUrl;

            if (isMarkComplete) {
                // Mark as complete - include dateNumber
                const dateNumber = getCurrentDateNumber();
                apiUrl = `/plan/updated-plans-completed-status?planIds=${planIds}&dateNumber=${dateNumber}`;
            } else {
                // Unmark as complete - don't include dateNumber
                apiUrl = `/plan/updated-plans-completed-status?planIds=${planIds}`;
            }

            const response = await Http.PUT(apiUrl, {});
            // Check if response exists and has status property
            if (response && (response.status === 200 || response.status === 201)) {
                let message;
                
                try {
                    if (isMultipleData) {
                        message = `Updated completion status for ${rowData.length} plan${rowData.length === 1 ? "" : "s"}.`;
                    } else {
                        const versionName = rowData?.versionName || `V${rowData?.versionNumber}`;
                        message = `Updated completion status for plan '${versionName}'.`;
                    }
                    
                    showToast(message, 'success');
                    
                    // Call the success callback to refresh grid
                    if (onSuccessfulUpdate) {
                        onSuccessfulUpdate();
                    }
                    if (setAllPlansPageReload) {
                        setAllPlansPageReload(new Date());
                    }
                    handelClose();
                } catch (successError) {
                    console.error('Error in success handling:', successError);
                    // Still show success message even if callback fails
                    showToast(message || 'Plan completion status updated.', 'success');
                    handelClose();
                }
            } else {
                // Handle non-200 status codes
                let errorMessage;
                if (isMultipleData) {
                    errorMessage = `Failed to update completion status for ${rowData.length} plan${rowData.length === 1 ? "" : "s"}.`;
                } else {
                    const versionName = rowData?.versionName || `V${rowData?.versionNumber}`;
                    errorMessage = `Failed to update completion status for plan '${versionName}'.`;
                }
                
                showToast(errorMessage, 'error');
                handelClose(); 
            }
        } 
        catch (error) {
            console.error('Error updating plans status:', error);
            
            // Check if this is actually a successful response that threw an error in parsing
            if (error.response && (error.response.status === 200 || error.response.status === 201)) {
                let message;
                if (isMultipleData) {
                    message = `Updated completion status for ${rowData.length} plan${rowData.length === 1 ? "" : "s"}.`;
                } else {
                    const versionName = rowData?.versionName || `V${rowData?.versionNumber}`;
                    message = `Updated completion status for plan '${versionName}'.`;
                }
                
                showToast(message, 'success');
                
                try {
                    if (onSuccessfulUpdate) {
                        onSuccessfulUpdate();
                    }
                    if (setAllPlansPageReload) {
                        setAllPlansPageReload(new Date());
                    }
                } catch (callbackError) {
                    console.error('Error in callback execution:', callbackError);
                }
                
                handelClose();
                return; // Exit early since this was actually successful
            }
            
            // Handle actual errors
            let errorMessage;
            if (isMultipleData) {
                errorMessage = `Failed to update completion status for ${rowData.length} plan${rowData.length === 1 ? "" : "s"}.`;
            } else {
                const versionName = rowData?.versionName || `V${rowData?.versionNumber}`;
                errorMessage = `Failed to update completion status for plan '${versionName}'.`;
            }
            
            showToast(errorMessage, 'error');
            handelClose(); 
        }
        finally {
            setIsLoading(false);
            setCurrentModalName(null); 
        }
    };

    // Check if update button should be disabled
    const isUpdateDisabled = isLoading || (initialCompletedState && isMarkComplete);

    return (
        <div>
            <Dialog
                open={isOpen}
                fullWidth maxWidth="sm"
            >

                <Box className="!p-4">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <DialogTitle
                            id="planversion-dialog-title"
                            variant="h4"

                            sx={{
                                p: 0,
                                width: 496,
                                fontSize: "24px",
                                lineHeight: "36px",
                                fontWeight: 700,
                                color: "#212B36",
                            }}
                            display="flex"
                            gap="8px"
                        >
                            Mark complete or incomplete
                        </DialogTitle>
                        <IconButton
                            onClick={handelClose}
                        >
                            <ClearIcon sx={{ width: "16px", height: "16px" }} />
                        </IconButton>
                    </Box>
                    <DialogContent className='!p-0'>
                        
                        {!isMultipleData ? (
                            // Single data display
                            <Typography
                                sx={{ color: "#637381", fontSize: "12px", fontWeight: 400 }}
                            >
                                {property?.name || "Error"} | {rowData?.planTypeName || rowData?.planType} | {rowData?.fiscalYear} | {rowData?.versionName} | V{rowData?.versionNumber}
                            </Typography>
                        ) : (() => {
                            // Group the data first
                            const groupedData = Object.values(
                                rowData.reduce((acc, row) => {
                                    const propertyId = row.propertyId  || "NA";
                                    const propertyName = row.propertyId  ? dataService.getPropertyById(row.propertyId)?.name : "NA";
                                    if (!acc[propertyId]) {
                                        acc[propertyId] = { propertyName, items: [] };
                                    }
                                    acc[propertyId].items.push(row);
                                    return acc;
                                }, {})
                            );
                            // console.log("groupedData ------>", groupedData)

                            // Check if there's only one group with one item
                            if (groupedData.length === 1 && groupedData[0].items.length === 1) {
                                const singleRow = groupedData[0].items[0];
                                const singleProperty = dataService.getPropertyById(singleRow.propertyId);
                                const singlePropertyRow = dataService.getPropertyByIds(singleRow.propertyIds);
                                return (
                                    <Typography
                                        sx={{ color: "#637381", fontSize: "12px", fontWeight: 400 }}
                                    >
                                        {singleProperty?.name ||  singlePropertyRow?.name || "Error"} | {singleRow.planTypeName || singleRow.planType} | {singleRow.fiscalYear} | {singleRow.versionName} | V{singleRow.versionNumber}
                                    </Typography>
                                );
                            }

                            // Multiple data display
                            return (
                                <div className='border-b pb-4 border-[#919EAB33]'>
                                    <div className='mt-4 text-sm text-[#637381] leading-[22px]'>
                                        The following plan{rowData.length === 1 ? "'s" : "s'"} completion state will be updated
                                    </div>
                                    <div className="max-h-[245px] overflow-y-auto bg-[#F4F6F8] p-2 mt-2 rounded">
                                        {groupedData.map((group, index) => (
                                            <Box key={index} className="">
                                                <Typography
                                                    sx={{
                                                        color: "#212B36",
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                        marginBottom: "4px"
                                                    }}
                                                >
                                                    {group.propertyName}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        paddingLeft: 2,
                                                        fontSize: "12px",
                                                        lineHeight: "18px",
                                                        fontWeight: 400,
                                                        marginBottom: "8px"
                                                    }}
                                                >
                                                    <ul className="space-y-2" style={{ listStyleType: "disc", marginLeft: "20px" }}>
                                                        {group.items.map((rowData, rowIndex) => (
                                                            <li key={rowIndex} className=" !flex !items-center">
                                                                {rowData.planTypeName || rowData.planType} | {rowData.fiscalYear} | {rowData.versionName} | V{rowData.versionNumber}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </Box>
                                            </Box>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                        <FormControlLabel
                            className='!ms-0 !me-0 !my-4'
                            control={
                                <Checkbox
                                    size="small"
                                    checked={isMarkComplete}
                                    onChange={(e) => setIsMarkComplete(e.target.checked)}
                                    name="readOnly"
                                    sx={{ m: "1px" }}
                                />
                            }
                            label="Mark complete"
                            sx={{
                                display: "block",
                                ".MuiFormControlLabel-label": {
                                    fontSize: "14px !important",
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            p: 0,
                        }}
                    >
                        <Button
                            onClick={handelClose}
                            className='!py-[5px]'
                            sx={{ color: "#212B36", borderColor: "#919EAB52" , fontWeight: 700 , lineHeight: "24px"}}
                            variant="outlined"
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdate}
                            variant="contained"
                            className={` !font-bold text-sm leading-[24px]`}
                            disabled={isUpdateDisabled}
                        >
                            Update
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </div>
    );
}

export default MarkCompleteDialog